import { match } from "path-to-regexp";
import type { Location } from "react-router-dom";
import { EnumPersonRole } from "~/generated/graphql";
import { logger } from "../../utils/logger";
import { hasRequiredRoles } from "../../utils/loginChecks";
import { getFallBackRouteMatch } from "./utils/fallbackRouteMatch";
import { getFlattenedRoutesAndAddRoleRestrictionsFromSideBar } from "./utils/flattenRoutesAndAddRoleRestrictionsFromSideBar";

export enum EnumNavStatus {
  ALLOWED = "ALLOWED",
  DENIED = "DENIED",
  NOT_FOUND = "NOT_FOUND",
  FALLBACK_REDIRECT = "FALLBACK_REDIRECT",
}

export type NavStatus = {
  status: EnumNavStatus;
  redirectURL: string;
};

// this allows comparison between dynamic url elements (ie. '/:id') and their generated value
// e.g. admin/persons/:id --matches-- admin/persons/12gh3h4j4jjjjffff23
const matchPath = (routePath: string, currentPath: string): boolean => {
  const matcher = match(routePath, { decode: decodeURIComponent });
  return matcher(currentPath) !== false;
};

export const restrictRoutesAndFeatures = (location: Location, role?: EnumPersonRole): NavStatus => {
  // these routes can't be derived from the sidebar, but don't need protecting anyway
  if (
    location.pathname === "/" ||
    location.pathname === "/profile" ||
    location.pathname === "/logout" ||
    location.pathname === "/home" ||
    location.pathname === "/auth/login" ||
    location.pathname === "/manage-logins"
  )
    return { status: EnumNavStatus.ALLOWED, redirectURL: "" };

  const flattenedRoutesAndRoleRestrictionsFromSideBar = getFlattenedRoutesAndAddRoleRestrictionsFromSideBar();

  const currentRoute = flattenedRoutesAndRoleRestrictionsFromSideBar.find(
    // check the current url location against compiled routes objetcs
    route => matchPath(route.path, location.pathname),
  );

  if (currentRoute) {
    // if no route restrictions are present return TRUE
    if (currentRoute.restrictToRoles && currentRoute.restrictToRoles.length < 1) {
      logger.log("no restrictions:", currentRoute.path);
      return { status: EnumNavStatus.ALLOWED, redirectURL: "" };
    }
    // if user is an admin then don't restrict route
    if (role === EnumPersonRole.ADMIN) {
      logger.log("user can access:", currentRoute.path);
      return { status: EnumNavStatus.ALLOWED, redirectURL: "" };
    }
    // if user role isn't in allowed roles then check for a child route that is allowed
    // this is because parent sidebar routes have default links which are sometimes restricted
    // i.e. clicking Admin (parent-level navbar link) links a user to the Users page, but this page is restricted for most users
    // however Admin>Events is allowed for more users so try and look for a child route that is allowed
    if (!hasRequiredRoles(currentRoute.restrictToRoles)) {
      // check for fallback child link
      const fallBack = getFallBackRouteMatch(currentRoute);
      logger.log("restriction prevented access:", currentRoute.path);
      // if fallback route exists then redirect the user to that
      if (fallBack) {
        return {
          status: EnumNavStatus.FALLBACK_REDIRECT,
          redirectURL: fallBack,
        };
      }
      // if fallback route deson't exist then deny navigation and notify the user
      logger.log("restriction prevented access:", currentRoute.path);
      return { status: EnumNavStatus.DENIED, redirectURL: "" };
    }
    // if no restrictions and route exists
    logger.log("user can access:", currentRoute.path);
    return { status: EnumNavStatus.ALLOWED, redirectURL: "" };
  }

  // if route doesn't exist
  logger.log("No current route", location.pathname);
  return { status: EnumNavStatus.NOT_FOUND, redirectURL: "" };
};
